import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Building2, MapPin, Star, BedDouble, ShieldCheck, Calendar, Loader2, ClipboardList, CheckCircle2, ClipboardCheck, ChevronRight, Pencil, AlertTriangle, Trash2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ExportButton } from "@/pages/Report";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import EntityFormDialog from "@/components/EntityFormDialog";
import RoomsTab from "@/components/RoomsTab";
import { FullSparkline } from "@/components/Sparkline";
import { AIInsightCard } from "@/components/AIChat";

export default function StrutturaDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hotel, setHotel] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [todayChecks, setTodayChecks] = useState([]);
  const [auditTimeline, setAuditTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("30");

  // Edit anagrafica
  const canEdit = user?.role === "super_admin";
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [rms, setRms] = useState([]);
  const [capiArea, setCapiArea] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [h, d, c, a] = await Promise.all([
          api.get(`/hotels/${id}`),
          api.get(`/departments?hotel_id=${id}`),
          api.get(`/checks`, { params: { hotel_id: id, data_da: new Date().toISOString().slice(0, 10), data_a: new Date().toISOString().slice(0, 10) } }),
          api.get(`/hotels/${id}/audit-timeline`),
        ]);
        setHotel(h.data);
        setDepartments(d.data || []);
        setTodayChecks(c.data || []);
        setAuditTimeline(a.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Carica RM + capi area solo se super_admin (per la select nel modal)
  useEffect(() => {
    if (!canEdit) return;
    Promise.all([
      api.get("/users?role=resident_manager").catch(() => ({ data: [] })),
      api.get("/users?role=capo_area").catch(() => ({ data: [] })),
    ]).then(([r, c]) => {
      setRms((r.data || []).filter((u) => u.attivo !== false));
      setCapiArea((c.data || []).filter((u) => u.attivo !== false));
    });
  }, [canEdit]);

  const openEdit = () => {
    setEditForm({
      nome: hotel.nome || "",
      indirizzo: hotel.indirizzo || "",
      citta: hotel.citta || "",
      numero_camere: hotel.numero_camere || 1,
      categoria_stelle: hotel.categoria_stelle || 3,
      target_camere_pct: hotel.target_camere_pct ?? 20,
      produttivita_soglia_verde: hotel.produttivita_soglia_verde ?? 2.4,
      produttivita_soglia_gialla: hotel.produttivita_soglia_gialla ?? 2.0,
      resident_manager_id: hotel.resident_manager_id || "",
      capo_area_id: hotel.capo_area_id || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    // Validazioni frontend
    if (!editForm.nome.trim()) return toast.error("Nome obbligatorio");
    if (!editForm.indirizzo.trim()) return toast.error("Indirizzo obbligatorio");
    if (!editForm.citta.trim()) return toast.error("Città obbligatoria");
    const cam = parseInt(editForm.numero_camere, 10);
    if (!cam || cam < 1) return toast.error("Numero camere deve essere ≥ 1");
    const stelle = parseInt(editForm.categoria_stelle, 10);
    if (![3, 4, 5].includes(stelle)) return toast.error("Categoria stelle 3-5");
    const targetPct = parseInt(editForm.target_camere_pct, 10);
    if (!targetPct || targetPct < 5 || targetPct > 100) return toast.error("Target camere: 5-100%");
    const sogliaVerde = parseFloat(editForm.produttivita_soglia_verde);
    const sogliaGialla = parseFloat(editForm.produttivita_soglia_gialla);
    if (isNaN(sogliaVerde) || sogliaVerde < 0.5 || sogliaVerde > 10) return toast.error("Soglia verde: 0.5-10.0");
    if (isNaN(sogliaGialla) || sogliaGialla < 0.5 || sogliaGialla > 10) return toast.error("Soglia gialla: 0.5-10.0");
    if (sogliaGialla >= sogliaVerde) return toast.error("La soglia gialla deve essere inferiore alla verde");
    setSaving(true);
    try {
      const payload = {
        nome: editForm.nome.trim(),
        indirizzo: editForm.indirizzo.trim(),
        citta: editForm.citta.trim(),
        numero_camere: cam,
        categoria_stelle: stelle,
        target_camere_pct: targetPct,
        produttivita_soglia_verde: sogliaVerde,
        produttivita_soglia_gialla: sogliaGialla,
        resident_manager_id: editForm.resident_manager_id || null,
        capo_area_id: editForm.capo_area_id || null,
      };
      const prevTarget = hotel.target_camere_pct ?? 20;
      const prevVerde = hotel.produttivita_soglia_verde ?? 2.4;
      const prevGialla = hotel.produttivita_soglia_gialla ?? 2.0;
      const { data } = await api.patch(`/hotels/${id}`, payload);
      setHotel(data);
      if (prevVerde !== sogliaVerde || prevGialla !== sogliaGialla) {
        toast.success(`Soglie produttività aggiornate: V ${sogliaVerde} · G ${sogliaGialla}`);
      } else if (prevTarget !== targetPct) {
        toast.success(`Target aggiornato al ${targetPct}% — attivo da subito`);
      } else {
        toast.success("Struttura aggiornata");
      }
      setEditOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const nextAudit = auditTimeline.find((x) => x.stato === "pianificato" || x.stato === "in_corso");
  const lastCompleted = auditTimeline.find((x) => x.stato === "completato");
  const daysUntilNext = (() => {
    if (lastCompleted?.prossimo_audit_date) {
      const d = new Date(lastCompleted.prossimo_audit_date);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
    }
    return null;
  })();

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>;
  if (!hotel) return <div>Struttura non trovata.</div>;

  const STATO_PILL = {
    da_fare: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
    completato: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
    non_eseguito: "bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300",
  };

  return (
    <div className="space-y-6 ih-anim-in" data-testid="struttura-dettaglio">
      <Button variant="ghost" onClick={() => navigate(-1)} className="gap-1"><ArrowLeft className="h-4 w-4" /> Indietro</Button>

      <div className="ih-glass rounded-2xl p-7">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-2"><Building2 className="h-3 w-3" /> Struttura</div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light mb-2">{hotel.nome}</h1>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {hotel.indirizzo}</span>
              <span className="flex items-center gap-1.5"><BedDouble className="h-4 w-4" /> {hotel.numero_camere} camere</span>
              <span data-testid="hotel-target-pct" className="flex items-center gap-1.5 text-lavender-700 dark:text-lavender-300"><span className="text-xs uppercase tracking-wider">Target</span> {hotel.target_camere_pct ?? 20}%</span>
              <span className="flex items-center gap-1.5">{Array.from({ length: hotel.categoria_stelle }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}</span>
            </div>
            {hotel.aree_comuni && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="aree-comuni-chips">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1 self-center">Aree comuni:</span>
                {hotel.aree_comuni.split(",").map((a, i) => {
                  const trimmed = a.trim();
                  if (!trimmed) return null;
                  return (
                    <span key={i} className="px-2 py-0.5 rounded-full text-[11px] bg-lavender-50 dark:bg-lavender-900/20 text-lavender-700 dark:text-lavender-300">
                      {trimmed}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {canEdit && (
              <Button variant="outline" size="sm" onClick={openEdit} data-testid="edit-struttura-btn">
                <Pencil className="h-4 w-4" /> Modifica
              </Button>
            )}
            <ExportButton url={`/reports-export/hotel-monthly?hotel_id=${id}&anno=${new Date().getFullYear()}&mese=${new Date().getMonth() + 1}&format=pdf`} filename={`ih-auditflow-${hotel.nome.toLowerCase().replace(/ /g, "-")}.pdf`} label="PDF" />
            <ExportButton url={`/reports-export/hotel-monthly?hotel_id=${id}&anno=${new Date().getFullYear()}&mese=${new Date().getMonth() + 1}&format=xlsx`} filename={`ih-auditflow-${hotel.nome.toLowerCase().replace(/ /g, "-")}.xlsx`} label="Excel" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="ih-glass rounded-2xl p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Punteggio qualità</div>
          <div className="font-display text-4xl tracking-tight font-medium">{hotel.punteggio_qualita != null ? Math.round(hotel.punteggio_qualita) : "—"}<span className="text-base text-muted-foreground font-normal">/100</span></div>
        </div>
        <div className="ih-glass rounded-2xl p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1"><Calendar className="h-3 w-3" /> Ultima ispezione</div>
          <div className="font-display text-xl tracking-tight font-medium">{hotel.ultima_ispezione ? new Date(hotel.ultima_ispezione).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "—"}</div>
        </div>
        <div className="ih-glass rounded-2xl p-5" data-testid="prossimo-audit-card">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 flex items-center gap-1"><ClipboardCheck className="h-3 w-3" /> Prossima ispezione</div>
          {nextAudit ? (
            <>
              <div className="font-display text-xl tracking-tight font-medium">In corso</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Stato: {nextAudit.stato.replaceAll("_", " ")}</div>
            </>
          ) : lastCompleted?.prossimo_audit_date ? (
            <>
              <div className="font-display text-xl tracking-tight font-medium">{new Date(lastCompleted.prossimo_audit_date).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}</div>
              <div className={`text-[11px] mt-0.5 ${daysUntilNext != null && daysUntilNext < 7 ? "text-rose-500" : "text-muted-foreground"}`}>{daysUntilNext != null ? (daysUntilNext >= 0 ? `tra ${daysUntilNext} giorni` : `in ritardo di ${Math.abs(daysUntilNext)} giorni`) : "—"}</div>
            </>
          ) : (
            <div className="font-display text-xl tracking-tight font-medium text-muted-foreground">—</div>
          )}
        </div>
        <div className="ih-glass rounded-2xl p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Reparti</div>
          <div className="font-display text-4xl tracking-tight font-medium">{departments.length}</div>
        </div>
      </div>

      <AIInsightCard
        endpoint="/ai/hotel-weekly-summary"
        params={{ hotel_id: id, settimane: 1 }}
        title="Insight settimanale AI"
        sections={["highlights", "to_monitor"]}
      />

      <Tabs defaultValue="trend" className="w-full">
        <TabsList data-testid="struttura-tabs">
          <TabsTrigger value="trend" data-testid="tab-trend">Trend qualità</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Storico audit</TabsTrigger>
          <TabsTrigger value="controlli" data-testid="tab-controlli-oggi">Controlli oggi · {todayChecks.length}</TabsTrigger>
          <TabsTrigger value="reparti" data-testid="tab-reparti">Reparti</TabsTrigger>
          <TabsTrigger value="camere" data-testid="tab-camere">Camere</TabsTrigger>
        </TabsList>

        <TabsContent value="trend">
          <div className="ih-glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl tracking-tight">Storico punteggio</h2>
              <div className="flex gap-1 ih-glass rounded-full p-1">
                {["30", "60", "90"].map((r) => (
                  <button key={r} data-testid={`range-${r}`} onClick={() => setRange(r)} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${range === r ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{r}gg</button>
                ))}
              </div>
            </div>
            <FullSparkline hotelId={id} days={parseInt(range)} height={260} />
          </div>
        </TabsContent>

        <TabsContent value="ispezione">
          <div className="ih-glass rounded-2xl p-6" data-testid="audit-timeline-panel">
            <h2 className="font-display text-xl tracking-tight mb-4 flex items-center gap-2"><ClipboardCheck className="h-4 w-4" /> Storico ispezioni Capo Area</h2>
            {auditTimeline.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessuna ispezione registrata per questa struttura.</p>
            ) : (
              <ol className="relative border-l border-border/60 ml-2 space-y-5">
                {auditTimeline.map((a) => {
                  const stateColor = a.stato === "completato"
                    ? "bg-emerald-500"
                    : a.stato === "in_corso"
                      ? "bg-amber-500"
                      : a.stato === "in_ritardo"
                        ? "bg-rose-500"
                        : "bg-slate-400";
                  const valutColor = a.valutazione_complessiva === "eccellente"
                    ? "text-emerald-600 dark:text-emerald-400"
                    : a.valutazione_complessiva === "buono"
                      ? "text-amber-600 dark:text-amber-400"
                      : a.valutazione_complessiva === "critico"
                        ? "text-rose-600 dark:text-rose-400"
                        : "text-muted-foreground";
                  return (
                    <li key={a.id} className="ml-4 group" data-testid={`audit-timeline-item-${a.id}`}>
                      <span className={`absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full ring-4 ring-background ${stateColor}`} />
                      <div onClick={() => navigate(`/ispezioni/${a.id}`)} className="rounded-xl border border-border/60 p-4 bg-background/40 hover:bg-accent/30 cursor-pointer transition-colors flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{new Date(a.data_creazione).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}</span>
                            <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">{a.stato.replaceAll("_", " ")}</span>
                          </div>
                          <div className="mt-1 flex items-baseline gap-3">
                            <span className="font-display text-2xl tracking-tight font-medium">{a.punteggio_totale != null ? `${a.punteggio_totale}` : "—"}<span className="text-xs text-muted-foreground font-normal">/100</span></span>
                            <span className={`text-xs font-medium uppercase tracking-wider ${valutColor}`}>{a.valutazione_complessiva || "in lavorazione"}</span>
                          </div>
                          {a.prossimo_audit_date && a.stato === "completato" && (
                            <div className="text-[11px] text-muted-foreground mt-1">Prossimo previsto: {new Date(a.prossimo_audit_date).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}</div>
                          )}
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </TabsContent>

        <TabsContent value="controlli">
          <div className="ih-glass rounded-2xl p-6">
            <h2 className="font-display text-xl tracking-tight mb-4 flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Controlli di oggi</h2>
            {todayChecks.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessun controllo previsto oggi.</p>
            ) : (
              <div className="space-y-2">
                {todayChecks.map((c) => (
                  <div key={c.id} onClick={() => navigate(`/controlli/${c.id}`)} className="rounded-xl border border-border/60 p-4 bg-background/40 hover:bg-accent/30 cursor-pointer transition-colors flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{c.categoria.replaceAll("_", " ")}</div>
                      <div className="font-medium text-sm">{c.titolo}</div>
                    </div>
                    {c.stato === "completato" && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${STATO_PILL[c.stato] || ""}`}>{c.stato.replaceAll("_", " ")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="reparti">
          <div className="ih-glass rounded-2xl p-6">
            <h2 className="font-display text-xl tracking-tight mb-4">Reparti</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {departments.map((d) => (
                <div key={d.id} className="rounded-xl border border-border/60 p-4 bg-background/40">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{d.tipo}</div>
                  <div className="font-medium">{d.nome}</div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="camere">
          <div className="ih-glass rounded-2xl p-6">
            <h2 className="font-display text-xl tracking-tight mb-4">Anagrafica camere</h2>
            {hotel && user && <RoomsTab hotel={hotel} currentUser={user} />}
          </div>
        </TabsContent>
      </Tabs>

      {/* Zona rossa — solo Super Admin */}
      {canEdit && hotel && (
        <DangerZone hotel={hotel} onReloaded={() => {}} />
      )}

      {/* Modal modifica struttura (super_admin) */}
      {canEdit && editForm && (
        <EntityFormDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          title="Modifica struttura"
          description="Aggiorna i dati anagrafici dell'hotel."
          size="md"
          onSubmit={submitEdit}
          submitLabel="Salva modifiche"
          saving={saving}
          testId="edit-struttura-form"
          submitTestId="edit-struttura-submit"
        >
          <div className="space-y-2">
            <Label>Nome</Label>
            <Input required value={editForm.nome} onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })} data-testid="edit-struttura-nome" />
          </div>
          <div className="space-y-2">
            <Label>Indirizzo</Label>
            <Input required value={editForm.indirizzo} onChange={(e) => setEditForm({ ...editForm, indirizzo: e.target.value })} data-testid="edit-struttura-indirizzo" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Città</Label>
              <Input required value={editForm.citta} onChange={(e) => setEditForm({ ...editForm, citta: e.target.value })} data-testid="edit-struttura-citta" />
            </div>
            <div className="space-y-2">
              <Label>Stelle</Label>
              <Select value={String(editForm.categoria_stelle)} onValueChange={(v) => setEditForm({ ...editForm, categoria_stelle: Number(v) })}>
                <SelectTrigger data-testid="edit-struttura-stelle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 stelle</SelectItem>
                  <SelectItem value="4">4 stelle</SelectItem>
                  <SelectItem value="5">5 stelle</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Numero camere</Label>
            <Input type="number" min={1} required value={editForm.numero_camere} onChange={(e) => setEditForm({ ...editForm, numero_camere: e.target.value })} data-testid="edit-struttura-camere" />
          </div>
          <div className="space-y-2">
            <Label>Target % controllo camere giornaliero</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={5}
                max={100}
                step={1}
                required
                value={editForm.target_camere_pct}
                onChange={(e) => setEditForm({ ...editForm, target_camere_pct: e.target.value })}
                data-testid="edit-struttura-target-pct"
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug">
              Percentuale di camere da controllare ogni giorno (default 20%). Range 5-100%. L'aggiornamento è immediato su tutte le viste.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Soglie produttività Housekeeping (camere/ora)</Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-1">Verde ≥</div>
                <Input
                  type="number"
                  min={0.5}
                  max={10}
                  step={0.1}
                  required
                  value={editForm.produttivita_soglia_verde}
                  onChange={(e) => setEditForm({ ...editForm, produttivita_soglia_verde: e.target.value })}
                  data-testid="edit-struttura-soglia-verde"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-1">Gialla ≥</div>
                <Input
                  type="number"
                  min={0.5}
                  max={10}
                  step={0.1}
                  required
                  value={editForm.produttivita_soglia_gialla}
                  onChange={(e) => setEditForm({ ...editForm, produttivita_soglia_gialla: e.target.value })}
                  data-testid="edit-struttura-soglia-gialla"
                />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug">
              Sopra la soglia verde = ottimo. Sotto la gialla = insufficiente. Default 2.4 / 2.0. La gialla deve essere inferiore alla verde.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Resident Manager</Label>
            <Select value={editForm.resident_manager_id || "__none__"} onValueChange={(v) => setEditForm({ ...editForm, resident_manager_id: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="edit-struttura-rm"><SelectValue placeholder="Nessuno" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Nessuno —</SelectItem>
                {rms.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Capo Area</Label>
            <Select value={editForm.capo_area_id || "__none__"} onValueChange={(v) => setEditForm({ ...editForm, capo_area_id: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="edit-struttura-ca"><SelectValue placeholder="Nessuno" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Nessuno —</SelectItem>
                {capiArea.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </EntityFormDialog>
      )}
    </div>
  );
}

// ─── Danger Zone — reset controlli + rigenerazione task ────────────────
function DangerZone({ hotel, onReloaded }) {
  const [resetOpen, setResetOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [regenLoading, setRegenLoading] = useState(null); // role attualmente in loading

  const nameMatches = confirmText.trim().toLowerCase() === (hotel?.nome || "").trim().toLowerCase();

  const doReset = async () => {
    if (!nameMatches) return;
    setResetting(true);
    try {
      const { data } = await api.delete(`/admin/hotels/${hotel.id}/daily-checks`, {
        data: { confirm_name: confirmText.trim() },
      });
      toast.success(`Controlli di ${data.hotel_nome} azzerati — ${data.deleted_count} task rimossi`);
      setResetOpen(false); setConfirmText("");
      onReloaded?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setResetting(false); }
  };

  const doRegenerate = async (role, label) => {
    if (!window.confirm(`Rigenerare i task giornalieri di ${label} per ${hotel.nome} oggi?`)) return;
    setRegenLoading(role);
    try {
      const { data } = await api.post(`/admin/hotels/${hotel.id}/regenerate-tasks`, { role });
      toast.success(`Task rigenerati per ${label} — ${data.created} task creati (${data.deleted} rimossi)`);
      onReloaded?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setRegenLoading(null); }
  };

  const roles = [
    { key: "resident_manager", label: "Resident Manager", assignedId: hotel.resident_manager_id },
    { key: "housekeeping_manager", label: "Housekeeping Manager", assignedId: hotel.housekeeping_manager_id || null },
    { key: "front_office_manager", label: "Front Office Manager", assignedId: hotel.front_office_manager_id || null },
  ];

  return (
    <section className="mt-8 rounded-2xl border border-rose-300 dark:border-rose-900/60 bg-rose-50/50 dark:bg-rose-950/20 p-6" data-testid="hotel-danger-zone">
      <div className="flex items-center gap-2 text-rose-700 dark:text-rose-300 mb-1">
        <AlertTriangle className="h-4 w-4" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">Zona Rossa · Azioni Amministrative</span>
      </div>
      <h2 className="font-display text-xl font-semibold mb-4 text-rose-900 dark:text-rose-100">
        Reset controlli e rigenerazione task
      </h2>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Colonna sinistra — reset totale */}
        <div className="rounded-xl bg-white/60 dark:bg-slate-900/40 border border-rose-200 dark:border-rose-900/60 p-4">
          <div className="text-sm font-semibold mb-1">Azzera controlli arretrati</div>
          <p className="text-xs text-muted-foreground mb-3">
            Elimina TUTTI i daily checks di questo hotel (arretrati, oggi, completati). Irreversibile.
          </p>
          <Button variant="destructive" size="sm" onClick={() => setResetOpen(true)} className="gap-1.5" data-testid="reset-checks-btn">
            <Trash2 className="h-3.5 w-3.5" /> Azzera controlli
          </Button>
        </div>

        {/* Colonna destra — rigenera task per ruolo */}
        <div className="rounded-xl bg-white/60 dark:bg-slate-900/40 border border-rose-200 dark:border-rose-900/60 p-4">
          <div className="text-sm font-semibold mb-1">Rigenera task oggi</div>
          <p className="text-xs text-muted-foreground mb-3">
            Cancella i task di oggi per il ruolo scelto e li ricrea dal template.
          </p>
          <div className="space-y-2">
            {roles.map((r) => {
              const disabled = !r.assignedId || regenLoading === r.key;
              return (
                <Button
                  key={r.key}
                  variant="outline" size="sm"
                  disabled={disabled}
                  onClick={() => doRegenerate(r.key, r.label)}
                  className="w-full justify-start gap-1.5"
                  title={!r.assignedId ? `Nessun ${r.label} assegnato` : undefined}
                  data-testid={`regen-${r.key}`}
                >
                  {regenLoading === r.key
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <RefreshCw className="h-3.5 w-3.5" />}
                  {r.label}
                  {!r.assignedId && <span className="text-[10px] text-muted-foreground ml-auto">non assegnato</span>}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="reset-checks-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-500" /> Conferma reset controlli
            </DialogTitle>
            <DialogDescription>
              Stai per eliminare <strong>TUTTI</strong> i controlli giornalieri di{" "}
              <strong>{hotel.nome}</strong> (arretrati, oggi e completati). Questa operazione è{" "}
              <strong>irreversibile</strong>. Digita il nome della struttura per confermare.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Digita: <code className="bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 px-1.5 rounded text-xs">{hotel.nome}</code></Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={hotel.nome}
              data-testid="reset-checks-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetOpen(false); setConfirmText(""); }}>Annulla</Button>
            <Button
              variant="destructive"
              disabled={!nameMatches || resetting}
              onClick={doReset}
              data-testid="reset-checks-confirm-btn"
            >
              {resetting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
